// validators/hosting.js

const hostingValidators = {
  // Step 2 → Title
  2: ({ step1 }, t) => {
    if (!step1.name?.trim()) {
      return t("please_fill_title");
    }
    if (step1.name.trim().length > 50) {
      return t("title_too_long");
    }
    return null;
  },

  // Step 3 → Hosting Type
  3: ({ step1 }, t) => {
    if (!step1.hosting_type_id) {
      return t("please_select_hosting_type");
    }
    return null;
  },

  // Step 4 → Location
  4: ({ step1 }, t) => {
    if (!step1.country_id) {
      return t("please_fill_country");
    }
    if (!step1.city_id) {
      return t("please_fill_city");
    }
    if (!step1.phone?.trim()) {
      return t("please_fill_phone");
    }
    if (!step1.address?.trim()) {
      return t("please_fill_address");
    }
    // Optional: Validate coordinates if provided
    if (
      (step1.latitude && !step1.longitude) ||
      (!step1.latitude && step1.longitude)
    ) {
      return t("please_fill_complete_coordinates");
    }
    return null;
  },

  // Step 5 → Informations
  5: ({ step1 }, t) => {
    if (!step1.number_of_travelers || step1.number_of_travelers < 1) {
      return t("invalid_number_of_travelers");
    }
    if (!step1.number_of_rooms || step1.number_of_rooms < 1) {
      return t("invalid_number_of_rooms");
    }
    if (!step1.number_of_bed || step1.number_of_bed < 1) {
      return t("invalid_number_of_beds");
    }
    if (!step1.number_of_bath_rooms || step1.number_of_bath_rooms < 1) {
      return t("invalid_number_of_bathrooms");
    }
    return null;
  },

  // Step 7 → Description
  7: ({ step2 }, t) => {
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary");
    }
    if (step2.short_summary.trim().length < 32) {
      return t("summary_too_short");
    }
    if (step2.short_summary.trim().length > 200) {
      return t("summary_too_long");
    }
    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description");
    }
    if (step2.detailed_description.trim().length < 60) {
      return t("description_too_short");
    }
    if (step2.detailed_description.trim().length > 500) {
      return t("description_too_long");
    }
    if (!step2.key_points || step2.key_points.length === 0) {
      return t("please_fill_key_points");
    }
    return null;
  },

  // Step 8 → Attributes/Equipment
  8: ({ step2 }, t) => {
    if (!step2.equipment || !Array.isArray(step2.equipment)) {
      return t("please_select_equipment");
    }
    if (step2.equipment.length === 0) {
      return t("please_select_at_least_one_equipment");
    }
    return null;
  },

  // Step 9 → Keywords
  9: ({ step2 }, t) => {
    if (!step2.keyword || !Array.isArray(step2.keyword)) {
      return t("please_add_keywords");
    }
    if (step2.keyword.length === 0) {
      return t("please_add_at_least_one_keyword");
    }
    return null;
  },

  // Step 11 → Availability
  11: ({ step3 }, t) => {
    // Validate dates
    if (!step3.start_date) {
      return t("please_fill_start_date");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date");
    }

    // Validate date range
    const startDate = new Date(step3.start_date);
    const endDate = new Date(step3.end_date);

    if (startDate >= endDate) {
      return t("end_date_must_be_after_start_date");
    }

    // Validate cutoff time
    if (
      !step3.cutoff_time_booking_value ||
      step3.cutoff_time_booking_value < 1
    ) {
      return t("invalid_cutoff_time_value");
    }

    if (!step3.cutoff_time_booking_unit) {
      return t("please_select_cutoff_time_unit");
    }

    // Validate cancellation policy
    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null ||
      step3.cancellation_policy_id === ""
    ) {
      return t("please_fill_cancellation_policy_id");
    }

    return null;
  },

  // Step 12 → Booking
  12: ({ step3 }, t) => {
    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode");
    }

    if (
      step3.booking_confirmation_mode !== "instant" &&
      step3.booking_confirmation_mode !== "manual"
    ) {
      return t("invalid_booking_confirmation_mode");
    }

    return null;
  },

  // Step 13 → Pricing
  13: ({ step3 }, t) => {
    const pricing = step3.pricing || {};

    if (!pricing.price || pricing.price <= 0) {
      return t("please_fill_valid_price");
    }

    if (!pricing.applicable_days || pricing.applicable_days.length === 0) {
      return t("please_select_applicable_days");
    }

    return null;
  },

  // Step 14 → Review (Final validation before submission)
  14: ({ step1, step2, step3 }, t) => {
    // Validate step1 fields
    if (!step1.name?.trim()) {
      return t("please_fill_title_before_submit");
    }

    if (!step1.hosting_type_id) {
      return t("please_select_hosting_type_before_submit");
    }

    if (!step1.country_id) {
      return t("please_fill_country_before_submit");
    }

    if (!step1.city_id) {
      return t("please_fill_city_before_submit");
    }

    if (!step1.number_of_travelers || step1.number_of_travelers < 1) {
      return t("invalid_number_of_travelers_before_submit");
    }

    // Validate step2 fields
    if (!step2.short_summary?.trim()) {
      return t("please_fill_summary_before_submit");
    }

    if (!step2.detailed_description?.trim()) {
      return t("please_fill_description_before_submit");
    }

    if (!step2.equipment || step2.equipment.length === 0) {
      return t("please_select_equipment_before_submit");
    }

    if (!step2.keyword || step2.keyword.length === 0) {
      return t("please_add_keywords_before_submit");
    }

    // Validate step3 fields
    if (!step3.start_date) {
      return t("please_fill_start_date_before_submit");
    }

    if (!step3.end_date) {
      return t("please_fill_end_date_before_submit");
    }

    if (
      step3.cancellation_policy_id === undefined ||
      step3.cancellation_policy_id === null ||
      step3.cancellation_policy_id === ""
    ) {
      return t("please_fill_cancellation_policy_before_submit");
    }

    if (!step3.booking_confirmation_mode) {
      return t("please_fill_booking_confirmation_mode_before_submit");
    }

    const pricing = step3.pricing || {};
    if (!pricing.price || pricing.price <= 0) {
      return t("please_fill_valid_price_before_submit");
    }

    if (!pricing.applicable_days || pricing.applicable_days.length === 0) {
      return t("please_select_applicable_days_before_submit");
    }

    return null;
  },

  // No shared validators - everything is custom
  shared: [],
};

export default hostingValidators;
